import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AuditRunEntry, AuditRunsData } from '../../api/types.js';
import { dataStore } from '../../store/dataStore.js';
import { queries } from '../../store/queries.js';
import { color, font, fontSize, radius } from '../../theme/tokens.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { formatNumberExact } from '../MetricCell/format.js';
import { EmptyState } from '../States/States.js';
import { CadencePill, Cell, TrendGlyph, figure, formatRunDate, runTrend } from './shared.js';

/**
 * State 2 — one audit's runs (blueprint lines 1011–1045, PRD-Audit §6.4).
 *
 * ⚠ The rows are the dates the service ACTUALLY holds — three on 2026-09-27, where the blueprint
 * seeds sixty. Padding the table to look like the blueprint would fabricate history (FR-AU17).
 * "Load 10 more runs" appears only when the server says more exist.
 */

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: fontSize.control,
        fontWeight: 600,
        color: color.inkMuted,
        textDecoration: 'none',
        marginBottom: 14,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
        <path d="m15 18-6-6 6-6" />
      </svg>
      {label}
    </Link>
  );
}

export function AuditRuns({ data }: { data: AuditRunsData }) {
  const navigate = useNavigate();
  const { audit } = data;
  const reporting = audit.kind === 'reporting';
  const noun = reporting ? audit.labels.focus.short.toLowerCase() : 'flagged';

  // "Load 10 more" appends pages; UI state only, reset when the audit changes.
  const [more, setMore] = useState<AuditRunEntry[]>([]);
  const [hasMore, setHasMore] = useState(data.hasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    setMore([]);
    setHasMore(data.hasMore);
  }, [audit.id, data.hasMore]);

  const runs = [...data.runs, ...more];

  async function loadMore() {
    const oldest = runs.at(-1)?.runDate;
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const page = await dataStore.run(queries.auditRuns(audit.id, oldest, 10));
      setMore((m) => [...m, ...(page.data?.runs ?? [])]);
      setHasMore(page.data?.hasMore ?? false);
    } finally {
      setLoadingMore(false);
    }
  }

  const columns: Array<Column<AuditRunEntry>> = [
    {
      key: 'runDate',
      label: 'Run Date',
      fullLabel: 'Date this audit run evaluated',
      width: '1.1fr',
      align: 'left',
      render: (r) => (
        <Cell>
          <span style={{ fontWeight: 600, color: color.ink }}>{formatRunDate(r.runDate)}</span>
        </Cell>
      ),
    },
    {
      key: 'summary',
      label: 'Summary',
      fullLabel: 'One-line summary of what this run found',
      width: '2.2fr',
      align: 'left',
      render: (r) => (
        <Cell>
          <span style={{ fontSize: fontSize.bodyStrong, color: color.inkMuted }}>{r.summary}</span>
        </Cell>
      ),
    },
    {
      key: 'headline',
      label: reporting ? audit.labels.focus.short : 'Flagged',
      fullLabel: reporting
        ? `${audit.labels.focus.short} this run, vs. the prior run`
        : 'Recruiters flagged this run, vs. the prior run',
      width: '.9fr',
      align: 'right',
      render: (r) => {
        const value = reporting ? r.focusTotal : r.flagged;
        const trend = runTrend(r.delta, audit.direction.headline, r.previousRunDate, noun);
        const ink = reporting ? color.inkNumeric : value ? color.negative : color.inkMintStrong;
        return (
          <Cell align="right"><Disclosure
            accessibleName={`${value === null ? 'none' : formatNumberExact(value)} ${noun}. ${trend.tooltip}`}
            panel={
              <span>
                <strong>{value === null ? '—' : formatNumberExact(value)} {noun}</strong>
                <span style={{ display: 'block' }}>{trend.tooltip}</span>
              </span>
            }
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <span className="mono" style={{ fontFamily: font.mono, fontSize: fontSize.bodyStrong, fontWeight: 700, color: ink }}>
              {figure(value)}
            </span>
            <TrendGlyph trend={trend} />
          </Disclosure></Cell>
        );
      },
    },
  ];

  return (
    <div>
      <BackLink to="/audit" label="All Audits" />
      <div
        style={{
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: radius['6xl'],
          padding: '16px 20px',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
          <CadencePill cadence={audit.cadence} note={audit.cadenceNote} />
          <span style={{ fontSize: fontSize.caption, color: color.inkFaint }}>{audit.section}</span>
        </div>
        <div style={{ fontSize: fontSize.body, color: color.inkBody, lineHeight: 1.5 }}>
          {audit.description ??
            'An audit the audit service has started and this page does not describe yet. Its figures are shown with the service’s own labels.'}
        </div>
      </div>

      {runs.length === 0 ? (
        <EmptyState title="This audit hasn't produced results yet." />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={runs}
            rowKey={(r) => r.runDate}
            onRowClick={(r) => navigate(`/audit/${encodeURIComponent(audit.id)}/${r.runDate}`)}
            caption={`${audit.name} runs`}
            maxBodyHeight={false}
          />
          {hasMore ? (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 14,
                padding: 11,
                background: color.surface,
                border: `1px solid ${color.border}`,
                borderRadius: radius['3xl'],
                cursor: 'pointer',
                fontSize: fontSize.control,
                fontWeight: 600,
                color: color.brandTo,
                fontFamily: 'inherit',
              }}
            >
              {loadingMore ? 'Loading…' : 'Load 10 more runs'}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
